import React, { useContext, useEffect, useRef, useState } from 'react';
import { ApiContext } from '@api/api';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { GetCMRDetailDocResponse, GetCMRDetailResponse, Pageable } from '@api/ringil-api.v2';
import { stringDateTimeWithLocaleFromApiStringInTz } from '@utils/dateWithTz';
import { Note } from '@components/styles.tsx';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import SvgFile from '@icons/file-inverted.svg?react';
import RowButton from '@components/RingilComponents/RowButton.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import RingilDataTable, { ColumnConfig, PagedData } from '@components/RingilDataTable/RingilDataTable.tsx';
import CMREditForm from '@components/CMR/CMREditForm.tsx';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import COTimezone from '@components/FigmaComponents/COTimezone.tsx';
import { SecuredConfig, useSecuredDecoration } from '@components/security/securedDecoration';
import { downloadBase64File } from '@utils/download';

interface ComponentProps {
  expeditionId: string;
  readOnly: boolean;
  secured?: SecuredConfig;
}


const CMRTableTab: React.FC<ComponentProps> = ({ expeditionId, readOnly, secured }) => {
  const {
    createCMRFromExpeditionV2,
    generateCMRPDFV2,
    getCMRDetail,
  } = useContext(ApiContext);
  const { tr } = useTranslationLgs();
  const { decorate } = useSecuredDecoration(secured);
  const [data, setData] = useState<GetCMRDetailResponse>();
  const { loggedUser } = useContext(AuthenticatedUserContext);

  const tableRef = useRef<{ reloadData: () => void, selectedObjects: GetCMRDetailDocResponse[], clear: () => void }>(null);

  useEffect(() => {
    reload();
  }, []);

  useEffect(() => {
    tableRef.current?.reloadData();
  }, [data?.documents]);

  const reload = () => {
    getCMRDetail(expeditionId, d => {
      setData(d);
    });
  };

  const regenerateCMRs = () => {
    createCMRFromExpeditionV2(expeditionId, () => {
      reload();
    });
  };

  const downloadFile = (cmrId: string) => {
    generateCMRPDFV2(cmrId, data => {
      downloadBase64File(data.data, data.mimeType, data.fileName);
    });
  };

  const renderApplicationIdColumn = (row: GetCMRDetailDocResponse) => {
    return <div>{row.applicationId}</div>;
  };

  const renderCreatedByColumn = (row: GetCMRDetailDocResponse) => {
    return (
      <>
        <div>{row.createdByFullName}</div>
        <Note>{row.createdByCompanyName}</Note>
      </>
    );
  };

  const renderCreatedAtColumn = (row: GetCMRDetailDocResponse) => {
    return (
      <div className="flex-col">
        <div>{stringDateTimeWithLocaleFromApiStringInTz(row.createdDate, loggedUser.defaultTimezoneId)}</div>
        <COTimezone timezoneId={loggedUser.defaultTimezoneId} />
      </div>
    );
  };

  const renderActionsColumn = (row: GetCMRDetailDocResponse, toggleExpandedRow: (row: GetCMRDetailDocResponse) => void) => {
    return (
      <div className="flex flex-row justify-end px-2 gap-2">
        <RingilButton mode={'table'} label={tr(`CMRTable.edit`, `Edit`)} onClick={() => toggleExpandedRow(row)} />
        <RingilButton mode={'table'} label={tr(`CMRTable.download`, `Download`)} onClick={() => downloadFile(row.id)} />
      </div>
    );
  };

  const loadDataApi = (filter: any, pageable: Pageable, onSuccess: (pagedData: PagedData<GetCMRDetailDocResponse>) => void) => {
    const page = pageable.pageNumber || 1;
    const size = pageable.pageSize || 10;
    const start = (page - 1) * size;
    const end = start + size;
    const items = data?.documents.slice(start, end);

    const pagedData: PagedData<GetCMRDetailDocResponse> = {
      data: items,
      total: data?.documents?.length || 0,
      page,
      size,
    };

    onSuccess(pagedData);
  };


  const columnsConfig: ColumnConfig<GetCMRDetailDocResponse>[] = [
    {
      id: 'applicationId',
      header: 'CMRTable.columnHeaderApplicationId',
      headerDefault: 'ID CMR',
      width: '25%',
      sortable: false,
      field: 'applicationId',
      order: 1,
      render: renderApplicationIdColumn,
    },
    {
      id: 'createdByFullName',
      header: 'CMRTable.columnHeaderCreatedBy',
      headerDefault: 'Created by',
      width: '25%',
      sortable: false,
      field: 'createdByFullName',
      order: 2,
      render: renderCreatedByColumn,
    },
    {
      id: 'createdDate',
      header: 'CMRTable.columnHeaderCreatedAt',
      headerDefault: 'Created at',
      width: '25%',
      sortable: false,
      field: 'createdDate',
      order: 3,
      render: renderCreatedAtColumn,
    },
    {
      id: 'actions',
      header: 'CMRTable.columnHeaderActions',
      headerDefault: '',
      width: '25%',
      sortable: false,
      order: 4,
      render: renderActionsColumn,
    },
  ];

  const rowExpansionTemplate = (row: GetCMRDetailDocResponse) => {
    return (
      <CMREditForm
        expeditionId={expeditionId}
        applicationId={row.applicationId}
        onSuccessEdit={() => {
          regenerateCMRs();
        }}
        originalData={row}
      />
    );
  };

  return (
    <>{decorate(<>
      <div className="flex flex-row mb-2 items-center">
        <COHeadline
          variant={'h2'}
          title={tr('CMRTable.cmrTableTitle', 'CMR Documents')}
          icon={<SvgFile className={'w-4 h-4 text-content-tertiary'} />}
        />
      </div>

      <RingilDataTable
        ref={tableRef}
        columnConfig={columnsConfig}
        loadDataFromApi={loadDataApi}
        filterName={'expedition_cmr_list'}
        rowExpansionTemplate={rowExpansionTemplate}
      />

      {readOnly === false && (
        <RowButton title={tr('CMRTable.generateCmrDocuments', 'Generate CMR Documents')} onClick={() => regenerateCMRs()} />
      )}
    </>)}</>
  );
};

export default CMRTableTab;
